import { SearchSideBarLayout } from 'components/SearchSideBar/SearchSidebarLayout';
import { FC } from 'react';
import { OverlaySearchArea } from 'components/SearchSideBar/OverlaySearchArea';
import { SearchButtonArea } from 'components/SearchSideBar/SearchButtonArea';
import { useShowOverlay } from 'components/SearchSideBar/hooks/useShowOverlay';
import { useCountUpdateAnimation } from 'hooks/useCountUpdateAnimation';
import { JobCountResponse } from '@doda/api-client/aspida/@types';
import { ModalOverlay } from 'components/ModalOverlay';
import { useDisclosure } from '@doda/common/src/hooks/useDisclosure';
import { InitialSideBarMasterDataType } from 'components/SearchSideBar/BasicInfo/types';
import { FeaturedSearchConditionQuery } from 'components/SearchSideBar/types';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { useAppSelector } from 'redux/hooks';
import { createOccupationDisplayData } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import {
  createDisplayArea,
  createDisplayCity,
} from 'components/LocationStationModal/LocationModal/logics/createLocationDisplayData';
import { createDisplayStation } from 'components/LocationStationModal/StationModal/logics/createStationDisplayData';
import { useEachModalJobCount } from 'hooks/useEachModalJobCount';
import dynamic from 'next/dynamic';
import { useSearchCondition } from './hooks/useSearchCondition';
import { useSearch } from './hooks/useSearch';
import { FeaturedModalMasterData } from '../interface';
import { BasicInfo } from './components/BasicInfo';
import { Details } from './components/Details';

const OccupationModal = dynamic(
  () =>
    import('components/OccupationSelectModal').then((module) => ({
      default: module.OccupationModal,
    })),
  { ssr: false }
);
const LocationModal = dynamic(
  () =>
    import('components/LocationStationModal/LocationModal').then((module) => ({
      default: module.LocationModal,
    })),
  { ssr: false }
);
const StationModal = dynamic(
  () =>
    import('components/LocationStationModal/StationModal').then((module) => ({
      default: module.StationModal,
    })),
  { ssr: false }
);

/**
 * Propsの定義
 */
interface Props {
  jobCountData: JobCountResponse;
  initialSideBarMasterData: InitialSideBarMasterDataType;
  modalMasterData: FeaturedModalMasterData['data'];
  featuredSearchCondition: FeaturedSearchConditionQuery;
}

export const FeaturedSearchSideBar: FC<Props> = ({
  jobCountData,
  initialSideBarMasterData,
  modalMasterData,
  featuredSearchCondition,
}) => {
  const { fid } = useAppSelector(appQueryParamsSelector);

  const {
    occupation,
    location,
    routeStation,
    jobOfferKind,
    updateJobCount,
    totalCount,
    selectedCurrentCondition,
    modalTotalCount,
    updateModalJobCount,
    isConditionUpperLimitError,
  } = useSearchCondition({
    initialSideBarMasterData,
    jobCountData,
    featuredSearchCondition,
  });

  const {
    onClickSearchButton,
    onClickOccupationModalSearchButton,
    onClickLocationModalSearchButton,
    onClickRoteStationModalSearchButton,
  } = useSearch(selectedCurrentCondition, fid);
  const { isOpacity, isInvisible, onChangeIsShowOverlay, observerRef } =
    useShowOverlay();
  const countAnimationRef = useCountUpdateAnimation(totalCount);

  const occupationModal = useDisclosure();
  const locationModal = useDisclosure();
  const stationModal = useDisclosure();

  const {
    occupationModalJobCount,
    locationAndStationModalJobCount,
    getJobCountWithoutOccupation,
    getJobCountWithoutLocationAndStation,
  } = useEachModalJobCount(selectedCurrentCondition);

  return (
    <>
      <SearchSideBarLayout isTopFollowing>
        <BasicInfo
          conditions={{
            occupation,
            location,
            routeStation,
          }}
          modalAction={{
            onOpenOccupationModal: occupationModal.onOpen,
            onOpenLocationModal: locationModal.onOpen,
            onOpenRouteModal: stationModal.onOpen,
          }}
          getJobCountAtModalDisplay={{
            getJobCountWithoutOccupation,
            getJobCountWithoutLocationAndStation,
          }}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <Details
          jobOfferKind={jobOfferKind}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <SearchButtonArea
          onClickSearchButton={() => onClickSearchButton()}
          observerRef={observerRef}
          countAnimationRef={countAnimationRef}
          validationFlags={{
            isConditionUpperLimitError,
          }}
        />
        <OverlaySearchArea
          totalCount={totalCount}
          isOpacity={isOpacity}
          isInvisible={isInvisible}
          onClickSearchButton={() => onClickSearchButton()}
          validationFlags={{
            isConditionUpperLimitError,
          }}
        />
      </SearchSideBarLayout>
      <ModalOverlay
        show={
          occupationModal.isOpen || locationModal.isOpen || stationModal.isOpen
        }
        onClickOverlay={() => {
          occupationModal.onClose();
          locationModal.onClose();
          stationModal.onClose();
        }}
      >
        <OccupationModal
          show={occupationModal.isOpen}
          onClickClose={occupationModal.onClose}
          onClickSearch={(checkedState) => {
            onClickOccupationModalSearchButton({
              searchJobTypeLId: checkedState.L,
              searchJobTypeMId: checkedState.M,
              searchJobTypeSId: checkedState.S,
            });
          }}
          onChangeSelectedOccupation={(checkedState) => {
            updateModalJobCount({
              searchJobTypeLId: checkedState.L,
              searchJobTypeMId: checkedState.M,
              searchJobTypeSId: checkedState.S,
            });
          }}
          displayOccupationData={createOccupationDisplayData(
            modalMasterData.occupation,
            occupationModalJobCount
          )}
          matchJobCount={modalTotalCount}
          submitButtonText="この条件で検索する"
          selectedOccupationIdList={{
            L: selectedCurrentCondition.searchJobTypeLId,
            M: selectedCurrentCondition.searchJobTypeMId,
            S: selectedCurrentCondition.searchJobTypeSId,
          }}
          validationFlags={{
            isConditionUpperLimitError,
          }}
        />
        <LocationModal
          show={locationModal.isOpen}
          onClickClose={locationModal.onClose}
          initialTotalCount={modalTotalCount}
          selectedLocationModalIdList={{
            checkedArea: selectedCurrentCondition.areaId,
            checkedPref: selectedCurrentCondition.prefectureId,
            checkedCity: selectedCurrentCondition.cityId,
            checkedWard: selectedCurrentCondition.wardId,
          }}
          areaList={createDisplayArea(
            modalMasterData.area,
            locationAndStationModalJobCount
          )}
          cityList={createDisplayCity(
            modalMasterData.area,
            locationAndStationModalJobCount
          )}
          onClickSearch={(checkedState) => {
            onClickLocationModalSearchButton({
              areaId: checkedState.checkedAllArea,
              prefectureId: checkedState.checkedAllPref,
              cityId: checkedState.checkedAllCity,
              wardId: checkedState.checkedWard,
            });
          }}
          onChangeSelectedLocation={(checkedState) => {
            updateModalJobCount({
              areaId: checkedState.checkedAllArea,
              prefectureId: checkedState.checkedAllPref,
              cityId: checkedState.checkedAllCity,
              wardId: checkedState.checkedWard,
            });
          }}
          submitButtonText="この条件で検索する"
          validationFlags={{
            isConditionUpperLimitError,
          }}
        />
        <StationModal
          show={stationModal.isOpen}
          onClickClose={stationModal.onClose}
          initialTotalCount={modalTotalCount}
          selectedStationModalIdList={{
            stationList: selectedCurrentCondition.stationId,
            routeList: selectedCurrentCondition.routeId,
          }}
          areaList={createDisplayArea(
            modalMasterData.area,
            locationAndStationModalJobCount
          )}
          railwayCompanyList={createDisplayStation(
            modalMasterData.route,
            locationAndStationModalJobCount
          )}
          onClickSearch={(checkedState) => {
            onClickRoteStationModalSearchButton({
              routeId: checkedState.checkedAllRoute,
              stationId: checkedState.checkedStation,
            });
          }}
          onChangeSelectedStation={(checkedState) => {
            updateModalJobCount({
              routeId: checkedState.checkedAllRoute,
              stationId: checkedState.checkedStation,
            });
          }}
          submitButtonText="この条件で検索する"
          validationFlags={{
            isConditionUpperLimitError,
          }}
        />
      </ModalOverlay>
    </>
  );
};
